import { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import { ArrowLeft, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { createAttachmentViewSession, fetchAttachmentBlobBySession } from '../core/index.js';

function getViewerSessionIdFromUrl() {
  const match = window.location.pathname.match(/^\/attachments\/view\/(.+)$/);
  return match ? match[1] : '';
}

const VIEWER_RETURN_KEY = 'rg_viewer_return';

export function setViewerReturnContext(referralId, returnView) {
  try {
    sessionStorage.setItem(VIEWER_RETURN_KEY, JSON.stringify({ referralId, returnView }));
  } catch { /* quota or private browsing */ }
}

export function consumeViewerReturnContext() {
  try {
    const raw = sessionStorage.getItem(VIEWER_RETURN_KEY);
    sessionStorage.removeItem(VIEWER_RETURN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

let _navigating = false;
export async function navigateToAttachmentViewer({ referralId, attachmentId, returnView }) {
  if (_navigating) return;
  _navigating = true;
  try {
    setViewerReturnContext(referralId, returnView);
    const { viewerSessionId } = await createAttachmentViewSession(referralId, attachmentId);
    window.location.href = `/attachments/view/${viewerSessionId}`;
  } catch (err) {
    _navigating = false;
    throw err;
  }
}

export default function AttachmentPdfViewer({ onClose }) {
  const viewerSessionId = getViewerSessionIdFromUrl();

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    if (!viewerSessionId) {
      setError('Missing viewer session in URL.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { blob } = await fetchAttachmentBlobBySession(viewerSessionId);
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (err) {
      setError(safeUserError(err) ?? 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [viewerSessionId]);

  useEffect(() => {
    load();
    return () => {
      setBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [load]);

  return (
    <div className="flex flex-col h-full bg-slate-900 min-h-screen">
      <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-800 border-b border-slate-700 shrink-0">
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span className="text-sm font-medium text-slate-200 truncate">
          Referral Document
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {loading && (
          <div className="flex flex-col items-center gap-3 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Loading document...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-4 text-slate-300 max-w-sm text-center">
            <AlertTriangle className="w-10 h-10 text-amber-400" />
            <p className="text-sm">{error}</p>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        )}

        {blobUrl && !loading && !error && (
          <iframe
            src={blobUrl}
            title="Referral Document"
            className="w-full h-full border-0"
          />
        )}
      </div>
    </div>
  );
}
